import type { CartPricingResponse } from '@repo/api/web/models'
import { defineStore } from 'pinia'

import { clampPointsToSpend, maxPointsToSpend } from '../utils/clampPointsToSpend'

export const usePromotionStore = defineStore('promotion', () => {
  const pricing = ref<CartPricingResponse | null>(null)
  const pricingLoading = ref(false)
  const pricingError = ref<unknown>(null)
  const pointsToSpend = ref(0)

  const activePromoCode = computed(
    () => pricing.value?.activePromoCode ?? null,
  )

  const canRedeemPoints = computed(() => {
    if (!pricing.value) {
      return false
    }

    return maxPointsToSpend(
      pricing.value.walletBalance ?? 0,
      pricing.value.maxPointsRedeemable ?? 0,
    ) > 0
  })

  function setPricing(value: CartPricingResponse | null) {
    pricing.value = value

    if (!value) {
      return
    }

    pointsToSpend.value = clampPointsToSpend(
      pointsToSpend.value,
      value.walletBalance ?? 0,
      value.maxPointsRedeemable ?? 0,
    )
  }

  function setPointsToSpend(value: number) {
    const walletBalance = pricing.value?.walletBalance ?? 0
    const maxRedeemable = pricing.value?.maxPointsRedeemable ?? 0
    pointsToSpend.value = clampPointsToSpend(value, walletBalance, maxRedeemable)
  }

  function setPricingLoading(value: boolean) {
    pricingLoading.value = value
  }

  function setPricingError(value: unknown) {
    pricingError.value = value
  }

  function resetPricing() {
    pricing.value = null
    pricingLoading.value = false
    pricingError.value = null
    pointsToSpend.value = 0
  }

  return {
    pricing,
    pricingLoading,
    pricingError,
    pointsToSpend,
    activePromoCode,
    canRedeemPoints,
    setPricing,
    setPointsToSpend,
    setPricingLoading,
    setPricingError,
    resetPricing,
  }
})
